<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { ref, watch, onMounted } from 'vue'
import type {
  IAoeCart,
  IAoeCartEarnedDiscount,
  IAoeCartItem,
} from '@/types/CartTypes.ts'
import {
  FreeDollarCouponAddToCart,
  FreeDollarCouponRemoveFromCart,
} from '@/services/CartService.ts'
import AoeSingleLineEntry from './Controls/AoeSingleLineEntry.vue'
import { FormatCurrency } from '@/services/currencyFormatter'
import { SetIsLoading } from '@/services/AoeHelpers'
import { SvgIcon } from '@progress/kendo-vue-common'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { Popup } from '@progress/kendo-vue-popup'
import { checkOutlineIcon, trashIcon } from '@progress/kendo-svg-icons'
import { DeleteItemFromCart } from '@/services/CartService'
import { useLogger } from 'vue-logger-plugin'
const logger = useLogger()

const showCouponPopup = ref(false)

const emit = defineEmits(['update-cart'])

const props = defineProps<{
  cart: IAoeCart
  earnedDiscount: IAoeCartEarnedDiscount
  FindCartItems(poeDiscPlanLevelDiscId: number): IAoeCartItem[]
  GetFullProductImageUrlFromStringDictionary(productCode: string): string
}>()

const displayMode = ref<'FreeDollars' | 'FreeDollarsOffOrder'>('FreeDollars')
const FreeDollarsOffOrderAmount = ref<number>(0)
const appliedCouponEarnedDiscount = ref<IAoeCartEarnedDiscount | null>(null)

function GetCouponFreeDollarsCost(poeDiscPlanLevelDiscId: number): number {
  const couponEarnedDiscount = props.cart?.earnedDiscounts?.find(
    earnedDiscount =>
      earnedDiscount.systemKeyword === 'Coupon' &&
      earnedDiscount.poeDiscPlanLevelDiscId === poeDiscPlanLevelDiscId,
  )
  return couponEarnedDiscount?.freeDollarsCost ?? 0
}

function AfterCartUpdate() {
  logger.debug('AfterCartUpdate')
  displayMode.value = props.cart?.earnedDiscounts?.some(
    earnedDiscount => earnedDiscount.systemKeyword === 'FreeDollarsOffOrder',
  )
    ? 'FreeDollarsOffOrder'
    : 'FreeDollars'

  const freeDollarsOffOrderDiscount = props.cart?.earnedDiscounts?.find(
    earnedDiscount => earnedDiscount.systemKeyword === 'FreeDollarsOffOrder',
  )
  FreeDollarsOffOrderAmount.value =
    freeDollarsOffOrderDiscount?.appliedAmount ?? 0

  appliedCouponEarnedDiscount.value =
    props.cart?.earnedDiscounts?.find(
      earnedDiscount =>
        earnedDiscount.systemKeyword === 'Coupon' &&
        earnedDiscount.freeDollarsCost != null &&
        earnedDiscount.freeDollarsCost > 0 &&
        earnedDiscount.isApplied,
    ) ?? null

  logger.debug('displayMode=', displayMode.value)
}

onMounted(() => {
  AfterCartUpdate()
})

watch(
  () => props.cart,
  () => {
    AfterCartUpdate()
  },
)

async function addToCartClickedHandler(
  event: MouseEvent,
  poeDiscPlanLevelDiscId: number,
) {
  console.log('addToCartClickedHandler', poeDiscPlanLevelDiscId)

  SetIsLoading(true)
  FreeDollarCouponAddToCart({
    aoeCartIdToken: props.cart?.aoeCartIdToken || '',
    poeDiscPlanLevelDiscId: poeDiscPlanLevelDiscId,
  }).then(() => {
    SetIsLoading(false)
    emit('update-cart', event)
    closePopupHandler()
  })
}

function closePopupHandler() {
  showCouponPopup.value = false
}

function removeFromCartClickedHandler(event: MouseEvent) {
  const poeDiscPlanLevelDiscId: number =
    appliedCouponEarnedDiscount.value?.poeDiscPlanLevelDiscId ?? 0

  SetIsLoading(true)
  FreeDollarCouponRemoveFromCart({
    aoeCartIdToken: props.cart?.aoeCartIdToken || '',
    poeDiscPlanLevelDiscId: poeDiscPlanLevelDiscId,
  }).then(() => {
    SetIsLoading(false)
    emit('update-cart', event)
  })
}

function updateCartHandler(event: MouseEvent) {
  logger.debug('updateCartHandler', event)
  emit('update-cart', event)
}

function deleteCartItem(event: MouseEvent, lineNumber: number) {
  logger.debug('deleteCartItem', lineNumber)

  SetIsLoading(true)
  DeleteItemFromCart({
    aoeCartIdToken: props.cart ? props.cart.aoeCartIdToken : '',
    lineNumber,
  }).then(() => {
    logger.debug('DeleteItemFromCart success')
    SetIsLoading(false)
    emit('update-cart', event)
  })
}
</script>

<template>
  <div class="row free-dollars-row">
    <div class="col-1 coupon-check">
      <SvgIcon class="check-mark-outline-icon" :icon="checkOutlineIcon">
      </SvgIcon>
    </div>
    <div
      class="col-7 discountMessage free-dollars-message"
      v-html="props.earnedDiscount.awardDescription"
    ></div>
    <div class="col-2" v-if="displayMode === 'FreeDollarsOffOrder'">
      {{ FormatCurrency(FreeDollarsOffOrderAmount, null) }}
    </div>
  </div>
  <div
    v-for="cartItem in displayMode === 'FreeDollars'
      ? props.FindCartItems(props.earnedDiscount.poeDiscPlanLevelDiscId)
      : []"
    :key="cartItem.lineNumber"
    class="row free-dollars-row"
  >
    <div class="col-1 free-dollars-row-chosen-check">
      <SvgIcon class="check-mark-outline-icon" :icon="checkOutlineIcon">
      </SvgIcon>
    </div>
    <div
      class="col-4 discountMessage free-dollars-row-message"
      v-html="props.earnedDiscount.awardMessage"
    ></div>
    <div class="col-1 free-dollars-row-chosen-image">
      <img
        :src="
          props.GetFullProductImageUrlFromStringDictionary(cartItem.productCode)
        "
        class="img-fluid prodImage"
        :alt="t('cart.ProductImageAlt')"
      />
    </div>
    <div class="col-5 free-dollars-row-chosen-name">
      {{ cartItem.skuName
      }}<span class="strike-through cart-item-retail-price">{{
        FormatCurrency(cartItem.price, null)
      }}</span
      ><span class="cart-item-price">{{
        FormatCurrency(
          cartItem.totalDiscountAmount
            ? cartItem.price - cartItem.totalDiscountAmount
            : cartItem.price,
          null,
        )
      }}</span>
    </div>
    <div class="col-1 free-dollars-row-chosen-delete">
      <KButton
        :fill-mode="'clear'"
        @click="deleteCartItem($event, cartItem.lineNumber)"
        :svg-icon="trashIcon"
        :theme-color="'error'"
        class="percent-off-item-delete-icon"
      ></KButton>
    </div>
  </div>

  <div
    class="row free-dollars-row"
    v-if="
      displayMode === 'FreeDollars' &&
      props.earnedDiscount.appliedAmount < props.earnedDiscount.totalAmount
    "
  >
    <div class="col-1"></div>
    <div class="col-12 discountMessage">
      {{ t('partyOrderEntry.FreeDollarItemSelect') }}
    </div>
    <div class="col-12 percent-off-item-entry-div">
      <AoeSingleLineEntry
        :cart="props.cart"
        :showheader="false"
        :poediscplanleveldiscid="props.earnedDiscount.poeDiscPlanLevelDiscId"
        @update-cart="updateCartHandler($event)"
        :hideQuantity="true"
      >
      </AoeSingleLineEntry>
    </div>
  </div>
  <div
    v-if="
      displayMode === 'FreeDollarsOffOrder' &&
      props.earnedDiscount.coupons != null &&
      props.earnedDiscount.coupons.length > 0
    "
    class="row mt-2 free-dollars-coupon-row"
  >
    <div class="col-1"></div>
    <div v-if="appliedCouponEarnedDiscount == null" class="col-11 col-md-6 free-dollar-coupon-message">
      {{ t('partyOrderEntry.FreeDollarCoupons') }}
    </div>
    <div v-else class="col-11 col-md-6 free-dollar-coupon-message">
      {{ t('partyOrderEntry.FreeDollarCouponsApplied', {description: appliedCouponEarnedDiscount?.awardDescription, couponAmount: FormatCurrency(appliedCouponEarnedDiscount?.freeDollarsCost ?? 0, null)}) }}
    </div>
    <div
      v-if="appliedCouponEarnedDiscount == null"
      class="col-12 col-md-4 free-dollar-coupon-button"
    >
      <KButton :fill-mode="'link'" @click="showCouponPopup = true"
        >{{ t('partyOrderEntry.FreeDollarCouponsBtn') }}
      </KButton>
    </div>
    <div v-else class="col-12 col-md-4 free-dollar-coupon-button">
      <KButton :fill-mode="'link'" @click="removeFromCartClickedHandler($event)"
        >{{ t('partyOrderEntry.FreeDollarCouponsRemoveBtn') }}
      </KButton>
    </div>
  </div>

  <Popup :show="showCouponPopup" :popup-class="'aoe-free-dollar-popup-content'">
    <div class="row aoe-free-dollar-popup-content-close">
      <div class="col-12 closePopup">
        <KButton
          :fill-mode="'clear'"
          @click="closePopupHandler"
          class="popupCloseButton"
          >&#x2715;</KButton
        >
      </div>
    </div>
    <div class="row mt-3 aoe-free-dollar-popup-content-header">
      <div class="col-12">
        {{ t('partyOrderEntry.FreeDollarCouponsPopupHeader') }}
      </div>
    </div>
    <div class="row mt-3 aoe-free-dollar-popup-content-divider">
      <div class="col-12">
        <hr />
      </div>
    </div>
    <div
      v-for="(coupon, index) in props.earnedDiscount.coupons"
      :key="coupon.couponCode"
      class="row mt-3 free-dollar-coupon-row"
    >
      <div class="col-6 col-md-4">
        {{ coupon.name }}
      </div>
      <div class="col-6 col-md-8">
        {{ coupon.description }}
      </div>
      <div class="col-4 mt-2 aoe-free-dollar-popup-content-price">
        {{
          FormatCurrency(
            GetCouponFreeDollarsCost(coupon.poeDiscPlanLevelDiscId),
            null,
          )
        }}
      </div>
      <div class="col-8 mt-2 aoe-free-dollar-popup-content-button">
        <KButton
          @click="
            addToCartClickedHandler($event, coupon.poeDiscPlanLevelDiscId)
          "
          class="baseBtn primaryBtn free-dollar-coupon-add-to-cart-button"
          :rounded="'large'"
          :theme-color="'checkoutContinue'"
        >
          {{ t('labels.AddToCart') }}
        </KButton>
      </div>
      <div
        v-if="
          index <
          (props.earnedDiscount.coupons != undefined
            ? props.earnedDiscount.coupons?.length - 1
            : 0)
        "
        class="row mt-3"
      >
        <div class="col-12 coupon-divider available-coupon-divider">
          <hr />
        </div>
      </div>
    </div>
  </Popup>
</template>

<style lang="scss">
.aoe-free-dollar-popup-content {
  position: fixed;
  top: 50%;
  left: 50%;
  padding: $card-padding;
  transform: translate(-50%, -50%);

  @media (max-width: 768px) {
    width: 90vw;
  }

  .aoe-free-dollar-popup-content-price {
    display: flex;
    align-items: center;
  }

  .aoe-free-dollar-popup-content-button {
    display: flex;
    justify-content: flex-end;
  }
}

.closePopup {
  text-align: right;
}

.free-dollar-coupon-message {
  @include typography-body-large;
  align-content: center;
}

.free-dollars-coupon-row .k-button-link {
  @include typography-body-large;
  text-decoration: underline;
}

.free-dollars-coupon-row .k-button-text {
  color: var(--buttonPrimaryColor);
}

.aoe-free-dollar-popup-content-header {
  @include typography-h4;
  text-align: center;
}
</style>
